import {
    Component,
    ViewChild,
    OnInit,
    OnDestroy,
    Input,
    SimpleChanges,
    HostListener,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { OverlayPanel } from "primeng/overlaypanel";
import { SidebarModule } from "primeng/sidebar";
import { Sidebar } from "primeng/sidebar";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { UserService } from "@/_services";
import { Subject } from "rxjs";
import { OnboardingStatus } from "@/_models/onboarding-status.interface";
import { AccordionModule } from "primeng/accordion";
import { ResponsiveService } from "@/components/shared/responsive/responsive.service";

@Component({
    selector: "app-quick-start-guide",
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        OverlayPanelModule,
        SidebarModule,
        SharedComponentsModule,
        AccordionModule,
    ],
    styleUrls: ["./quick-start-guide.component.scss"],
    templateUrl: "./quick-start-guide.component.html",
})
export class QuickStartGuideComponent implements OnInit, OnDestroy {
    @ViewChild("op") op!: OverlayPanel;
    @ViewChild("sidebar") sidebar!: Sidebar;
    @Input() onboardingData: OnboardingStatus | null = null;
    @Input() asapFormId: string | null = null;

    private destroy$ = new Subject<void>();

    recipientsOpen = true;
    teamOpen = false;
    shareOpen = false;

    asapFormRecipientsUrl: string | null = null;
    qrCodeUrl: string | null = null;
    isMobile: boolean = false;
    sidebarVisible = false;

    constructor(
        private userService: UserService,
        private router: Router,
        private responsiveService: ResponsiveService,
    ) {}

    ngOnInit(): void {
        this.updateDerivedFromFormId(this.asapFormId);
        this.checkInitialScreenSize();
        this.responsiveService.responsiveChangeSubject.subscribe();
    }

    private checkInitialScreenSize() {
        if (this.responsiveService.isMobile !== undefined) {
            this.isMobile = this.responsiveService.isMobile;
        }
    }

    private ensureOneSectionOpen() {
        if (!this.recipientsOpen && !this.teamOpen && !this.shareOpen) {
            this.recipientsOpen = true;
        }
    }

    toggleSection(sectionName: "recipients" | "team" | "share") {
        const sections = {
            recipients: this.recipientsOpen,
            team: this.teamOpen,
            share: this.shareOpen,
        };

        const currentSection = sections[sectionName];

        if (!currentSection) {
            // Close all sections and open the selected one
            this.recipientsOpen = sectionName === "recipients";
            this.teamOpen = sectionName === "team";
            this.shareOpen = sectionName === "share";
        } else {
            // Close the current section and ensure one remains open
            this[`${sectionName}Open`] = false;
            this.ensureOneSectionOpen();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["asapFormId"]) {
            this.updateDerivedFromFormId(changes["asapFormId"].currentValue);
        }
    }

    private updateDerivedFromFormId(id: string | null) {
        if (id) {
            this.asapFormRecipientsUrl = `/assessments/${id}/edit/recipients`;
            this.qrCodeUrl = `/api/assessments/${id}/qr-code?timestamp=${Date.now()}`;
        } else {
            this.asapFormRecipientsUrl = null;
            this.qrCodeUrl = null;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggle(event: Event) {
        if (this.isMobile) {
            this.sidebarVisible = !this.sidebarVisible;
        } else {
            if (this.op) {
                this.op.toggle(event);
            }
        }
    }

    hide() {
        if (this.isMobile) {
            this.sidebarVisible = false;
        } else {
            if (this.op) this.op.hide();
        }
    }

    navigateToRecipients() {
        if (this.asapFormRecipientsUrl) {
            this.router.navigate([this.asapFormRecipientsUrl]);
        }
    }

    printQrCode() {
        if (this.asapFormId) {
            const qrCodeUrl = `/api/assessments/${this.asapFormId}/qr-code?format=pdf`;
            window.open(qrCodeUrl, "_blank");
            this.onboardingData.hasGeneratedQrCode = true;
            if (this.isMobile) {
                this.hide();
            }
        }
    }

    navigateToTeam() {
        this.router.navigate(["/profile/users"]);
        if (this.isMobile) {
            this.hide();
        }
    }

    get hasReportRecipients(): boolean {
        return this.onboardingData?.hasReportRecipients || false;
    }

    get hasAddedUsers(): boolean {
        return this.onboardingData?.hasAddedUsers || false;
    }

    get hasGeneratedQrCode(): boolean {
        return this.onboardingData?.hasGeneratedQrCode || false;
    }

    get completedSteps(): number {
        let count = 0;
        if (this.hasReportRecipients) count++;
        if (this.hasAddedUsers) count++;
        if (this.hasGeneratedQrCode) count++;
        return count;
    }

    getProgressData() {
        return {
            completedSteps: this.completedSteps,
            hasReportRecipients: this.hasReportRecipients,
            hasAddedUsers: this.hasAddedUsers,
            hasGeneratedQrCode: this.hasGeneratedQrCode,
            totalSteps: 3,
        };
    }
}
